import { inject, Injectable } from '@angular/core';
import {
	doc,
	DocumentReference,
	Firestore,
	getDoc,
} from '@angular/fire/firestore';
import { QrFormComponent } from '@shared/components/qr-form/qr-form.component';
import { ScannerOutput } from '@shared/components/qr-scanner/qr-scanner.component';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UserModel } from '@interfaces/user.model';
import { UserService } from './user.service';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { LoadingService } from './loading.service';

@Injectable({ providedIn: 'root' })
export class QrService {
	private readonly http = inject(HttpClient);
	private readonly firestore = inject(Firestore);
	private readonly messageService = inject(MessageService);
	private readonly dialogService = inject(DialogService);
	private readonly userService = inject(UserService);
	private readonly loadingService = inject(LoadingService);

	public ref: DynamicDialogRef | null = null;

	constructor() {
		console.log('QrService');
	}

	public async handleQRCode(qr: ScannerOutput): Promise<void> {
		console.log({ qr });

		if (!qr || !qr.type) return;

		const user = await getDoc(
			doc(
				this.firestore,
				`companies/${environment.companyId}/users/${qr.userId}`
			) as DocumentReference<UserModel>
		);

		if (!user.exists() || !user.data()) {
			this.messageService.add({
				severity: 'error',
				summary: 'Error',
				detail: 'Usuario no encontrado',
			});
			return;
		}

		switch (qr.type) {
			case 'user':
				await this.handleUserQR(qr.userId);
				break;

			case 'coupon':
				if (qr.userId && qr.couponId) {
					await this.handleUserCoupon(qr.userId, qr.couponId);
				} else {
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: 'No se pudo agregar el cupón al usuario',
					});
				}
		}
	}

	private async handleUserQR(userId: string): Promise<void> {
		this.ref = this.dialogService.open(QrFormComponent, {
			header: 'Agregar puntos',
			width: '80%',
			closable: false,
			focusOnShow: false,
			modal: true,
			data: {
				type: 'user',
			},
		});

		this.ref.onClose.subscribe(async (result: { saleId: number }) => {
			console.log({ result });
			if (!result) return;

			this.loadingService.show();

			const data = {
				companyId: environment.companyId,
				staffId: this.userService.user()!.id,
				userId,
				saleId: Number(result.saleId),
			};

			this.addSaleToUser(data).subscribe({
				next: (result) => {
					console.log({ result });
					this.messageService.add({
						severity: 'success',
						summary: `Puntos agregados con éxito!`,
						detail: `Usuario: ${userId}`,
					});
					this.loadingService.hide();
				},
				error: (error) => {
					console.error(error);
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: `No se pudo agregar la venta al usuario ${userId}: ${error.error.message}`,
					});
					this.loadingService.hide();
				},
			});
		});
	}

	private addSaleToUser(data: {
		companyId: string;
		staffId: string;
		userId: string;
		saleId: number;
	}): Observable<object> {
		return this.http.post(`${environment.api_url}/qr/add-sale`, data);
	}

	private async handleUserCoupon(
		userId: string,
		couponId: string
	): Promise<void> {
		console.log({ userId, couponId });

		this.ref = this.dialogService.open(QrFormComponent, {
			header: 'Agregar cupón',
			width: '80%',
			closable: false,
			focusOnShow: false,
			modal: true,
			data: {
				type: 'coupon',
			},
		});

		this.ref.onClose.subscribe(async (result: { saleId: number }) => {
			console.log({ result });
			if (!result) return;

			const data = {
				companyId: environment.companyId,
				staffId: this.userService.user()!.id,
				userId,
				saleId: Number(result.saleId),
				couponId,
			};

			this.loadingService.show();

			this.addCouponToUser(data).subscribe({
				next: (result) => {
					console.log({ result });

					this.messageService.add({
						severity: 'success',
						summary: `Cupón: ${couponId} agregado con éxito!`,
						detail: `Usuario: ${userId}, Cupón: ${couponId}`,
						life: 7000,
					});
					this.loadingService.hide();
				},
				error: (error) => {
					console.error(error);
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: `No se pudo agregar el cupón ${couponId} al usuario ${userId}: ${
							error.error.message ?? error.error
						}`,
						life: 7000,
					});
					this.loadingService.hide();
				},
			});
		});
	}

	private addCouponToUser(data: {
		companyId: string;
		staffId: string;
		userId: string;
		couponId: string;
		saleId: number;
	}): Observable<object> {
		return this.http.post(`${environment.api_url}/qr/add-coupon`, data);
	}

	public async createUser(email: string, dni: string): Promise<boolean> {
		const body = {
			companyId: environment.companyId,
			staffId: this.userService.user()!.id,
			email,
			dni,
		};

		return new Promise<boolean>((resolve) => {
			this.http
				.post(`${environment.api_url}/users/create-from-staff`, body)
				.subscribe({
					next: (response) => {
						const result = response as {
							status: number;
							message: string;
						};
						console.log({ response });
						if (result.status === 200) {
							this.messageService.add({
								severity: 'success',
								summary: `Usuario creado con éxito!`,
								detail: `Usuario: ${body.dni}`,
							});
							resolve(true);
						} else {
							console.log({ result });
							this.messageService.add({
								severity: 'error',
								summary: 'Error',
								detail: `No se pudo crear el usuario ${body.dni}`,
								key: 'user-toast',
							});
							resolve(false);
						}
					},
					error: (err) => {
						const result = err.error as {
							status: number;
							message: string;
						};

						this.messageService.add({
							severity: 'error',
							summary: 'Error',
							detail: result.message,
							key: 'user-toast',
						});
						resolve(false);
					},
				});
		});
	}
}
